/* @flow */
import * as React from "react";
import FeatureFlags from "../../FeatureFlags";
import styled, { ThemeConsumer } from "styled-components";
import Gettext from "../common/Gettext";
import magnify from "./magnify";
import ActiveStock from "./ActiveStock";
import { VerticalTicks, HorizontalTicks } from "./NextGenTicks";
import Surface from "./Surface";
import Line from "./Line";
import Candlestick from "./Candlestick";
import Bar from "./Bar";
import Indicator from "./Indicator";
import OscillatorVisuals, {
  getRelativeHeight,
  getOscillatorHeightInPercents
} from "./OscillatorVisuals";
import Deal, { dealDuplicatesReducer } from "./Deal";
import Dot from "./Dot";
import background from "./background.png";
import { HorizontalBar, VerticalBar, Scope } from "./Bars";
import Arrow from "./Arrow";
import Sentiments from "./Sentiments";
import ChartBottomNavigation from "./ChartBottomNavigation";
import HorizontalLine from "./GraphicalTools/HorizontalLine";
import TourHint from "./TourHint";
import SocialTrading from "../SocialTrading/Desktop";
import OnLine, { OfflineBadge } from "../OnLine";

import BonusTimer from "./BonusTimer";
import ABTest from "../ABTest";

import * as Helpers from "./NextGenChartMath";
import * as Types from "./NextGenChartTypes";

const SurfaceContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

function Underlay(props: Types.VisualProps) {
  const start = props.scaleX.invert(0);
  const finish = props.scaleX.invert(props.width);
  return (
    <React.Fragment>
      <ActiveStock>{props.stock.stock}</ActiveStock>
      {FeatureFlags.debugHistoryFrames &&
        props.sections.map(section => {
          const [from, to] = Helpers.getSectionBoundaries(section);
          const color =
            props.realtimeThreshold > to
              ? "green"
              : props.realtimeThreshold <= from
                ? "red"
                : "yellow";

          return (
            <div
              key={`${from}-${to}`}
              style={{
                position: "absolute",
                left: Math.floor(props.scaleX(from)) + 1,
                right: props.width - Math.floor(props.scaleX(to)) + 1,
                top: 100,
                bottom: 100,
                border: `1px solid ${color}`,
                color: color
              }}
            >
              {Helpers.getSectionKey(section)}
            </div>
          );
        })}

      {FeatureFlags.debugHistoryFrames && (
        <VerticalBar x={props.scaleX(props.cursor.date)} />
      )}

      {props.emptySections && typeof props.emptySections.map === "function" && props.emptySections.map(section => {
        const [from, to] = Helpers.getSectionBoundaries(section);
        return (
          <div
            key={`${from}-${to}`}
            style={{
              position: "absolute",
              left: Math.floor(props.scaleX(from)) + 1,
              right: props.width - Math.floor(props.scaleX(to)) + 1,
              top: 100,
              bottom: 100,
              // border: "1px solid red",
              color: props.settings.lineColor,
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            <Gettext>The market was closed at this time</Gettext>
          </div>
        );
      })}

      {FeatureFlags.debugWeekends &&
        props.checkpoints
          .filter(
            checkpoint => checkpoint.value > start && checkpoint.value < finish
          )
          .map(checkpoint => (
            <div
              key={"checkpoint" + checkpoint.value}
              style={{
                position: "relative",
                top: 0,
                bottom: 0,
                left: props.scaleX(checkpoint.value),
                borderLeftColor: "red",
                borderRightColor: "green",
                borderLeftWidth: checkpoint.type === "negative" ? 1 : 0,
                borderRightWidth: checkpoint.type === "positive" ? 1 : 0,
                borderStyle: "solid"
              }}
            />
          ))}
      <HorizontalTicks scaleX={props.scaleX} ticks={props.scaleX.ticks(6)} />
      <VerticalTicks scaleY={props.scaleY} ticks={props.scaleY.ticks(6)} />
    </React.Fragment>
  );
}

function fitsViewport(props: Types.VisualProps, vector) {
  const [x, y] = vector;
  return x >= 0 && x <= props.width && y >= 0 && y <= props.height;
}

function Overlay(props: Types.VisualProps) {
  const { scaleX, scaleY, settings, lastPoint, tradeMode, lineColor } = props;

  const [lastPointX, lastPointY, lastPointSource] = lastPoint || [];

  const estimatedDealEndingX = scaleX(
    lastPointSource.date + settings.currentInterval * 1000
  );

  const scope =
    props.settings.chartType === "line"
      ? magnify(
          props.points,
          props.mouseX,
          (point, mouseX) => point[0] - mouseX
        )
      : magnify(
          props.candles,
          props.mouseX,
          (candle, mouseX) => candle.close[0] - mouseX
        );

  const graphicalTools =
    props.settings.graphicalTools &&
    props.settings.graphicalTools[props.stock.stock];
  const shouldDisplayGraphicalTools =
    graphicalTools &&
    graphicalTools.enabled &&
    graphicalTools.tools &&
    graphicalTools.tools.length;

  const ticksHeight = 24;
  const minLevel = scaleY.domain()[0];
  const maxLevel = scaleY.invert(ticksHeight);

  return (
    <React.Fragment>
      {props.deals
        .reduce(dealDuplicatesReducer, [])
        .map((deal, index, deals) => (
          <Deal
            key={deal.id != null ? deal.id : index.toString(10)}
            {...deal}
            now={props.time}
            scaleX={scaleX}
            scaleY={scaleY}
            viewportHeight={props.height}
            viewportWidth={props.width}
            lastPoint={lastPoint}
            tradeMode={tradeMode}
            refundClickHandler={props.refundClickHandler}
            refundFetching={props.refundFetching}
          />
        ))}

      {lastPoint && (
        <React.Fragment>
          {fitsViewport(props, [lastPointX, 0]) && (
            <VerticalBar x={lastPointX}>
              <Gettext>Opening of the trade</Gettext>
            </VerticalBar>
          )}
          {estimatedDealEndingX - lastPointX > 20 &&
            fitsViewport(props, [estimatedDealEndingX, 0]) && (
              <VerticalBar x={estimatedDealEndingX}>
                <Gettext>Closing of the trade</Gettext>
              </VerticalBar>
            )}
          <HorizontalBar
            y={lastPointY}
            viewportHeight={props.height}
            value={lastPointSource.value}
          />
          {props.intent && (
            <Arrow
              intent={props.intent}
              x={lastPointX}
              y={lastPointY}
              height={props.height}
            />
          )}
        </React.Fragment>
      )}

      {props.settings.chartType === "line" && (
        <Dot x={lastPointX} y={lastPointY} color={lineColor} />
      )}

      {props.isHovered &&
        scope && (
          <Scope
            type={props.settings.chartType}
            value={scope}
            scaleX={scaleX}
            scaleY={scaleY}
            x={props.mouseX}
          />
        )}

      {props.settings.sentiments && <Sentiments stats={props.stats} />}

      {shouldDisplayGraphicalTools &&
        graphicalTools.tools.map((tool, index) => {
          if (tool.enabled === false) {
            return null;
          }

          if (tool.anchorValue < minLevel || tool.anchorValue > maxLevel) {
            // Do not display Line if it's not within visible range
            return null;
          }

          return (
            <HorizontalLine
              key={index}
              value={scaleY(tool.anchorValue)}
              color={tool.color}
              width={tool.width}
              locked={tool.locked === true}
              onDragEnd={e => {
                e.stopPropagation();
                e.preventDefault();

                const clientY =
                  e.type === "touchmove" ? e.touches[0].clientY : e.clientY;
                const SUPER_DUPER_MEGA_CRUTCH_OF_HEAD_HEIGHT_OR_WHATEVER = 157;
                let value = props.scaleY.invert(
                  clientY -
                    (props.rectTop ||
                      SUPER_DUPER_MEGA_CRUTCH_OF_HEAD_HEIGHT_OR_WHATEVER)
                );

                // Stick Line to the border levels
                if (value < minLevel) {
                  value = minLevel;
                } else if (value > maxLevel) {
                  value = maxLevel;
                }

                props.dispatch({
                  type: "UPDATE_GRAPHICAL_TOOL",
                  payload: {
                    stock: tool.stock,
                    index,
                    anchorValue: value
                  }
                });
              }}
            />
          );
        })}
        <OnLine>
          {status => !status && <OfflineBadge />}
        </OnLine>
    </React.Fragment>
  );
}

class Visuals extends React.Component<
  Types.VisualProps,
  { isShownCandleModal: boolean }
> {
  state = {
    isShownCandleModal: false
  };

  toggleCandleModal = () => {
    this.setState(state => ({
      isShownCandleModal: !state.isShownCandleModal
    }));
  };

  canUpdate = true;

  shouldComponentUpdate() {
    return this.canUpdate;
  }

  componentDidUpdate() {
    this.canUpdate = false;
    requestAnimationFrame(() => {
      this.canUpdate = true;
    });
  }

  render() {
    const {
      points,
      width,
      height,
      scaleX,
      scaleY,
      settings,
      theme
    } = this.props;

    let getOscillatorBlockHeight = () => 0;
    let getOscillatorHeight = () => 0;

    if (this.props.oscillators.length) {
      const oscillatorHeightInPercents = getOscillatorHeightInPercents(
        this.props.oscillators.length
      );
      getOscillatorBlockHeight = height =>
        getRelativeHeight(height, oscillatorHeightInPercents);
      getOscillatorHeight = height =>
        Math.floor(height / this.props.oscillators.length);
    }

    const oscillatorsHeight = getOscillatorBlockHeight(height);
    const chartHeight = height - oscillatorsHeight;
    const chartHeightInPercents = Math.floor((chartHeight * 100) / height);
    const oscillatorHeightInPercents = 100 - chartHeightInPercents;
    const oscillatorHeight = getOscillatorHeight(oscillatorsHeight);

    let lineColor =
      settings.lineColor === theme.background
        ? theme.foreground
        : settings.lineColor;

    return (
      <div
        style={{
          cursor:
            this.props.graphicalMode.graphicalTool != null
              ? "crosshair"
              : "default",
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
          display: "flex",
          flexDirection: "column"
        }}
        ref={this.props.outerCountainerRef}
      >
        <div
          style={{
            display: "flex",
            flex: 1,
            position: "relative",
            backgroundImage: `url(${background})`
          }}
          {...this.props.containerProps}
        >
          <Underlay {...this.props} />

          <div
            style={{ width, height, position: "absolute", overflow: "hidden" }}
          >
            {this.props.settings.chartType === "candlestick" && (
              <Candlestick
                candles={this.props.candles}
                viewportWidth={this.props.width}
              />
            )}

            {this.props.settings.chartType === "bar" && (
              <Bar
                candles={this.props.candles}
                viewportWidth={this.props.width}
              />
            )}

            <SurfaceContainer>
              <div
                style={{
                  height: `${chartHeightInPercents}%`,
                  marginBottom: -47
                }}
              >
                <Surface width={width} height={chartHeight}>
                  {this.props.settings.chartType === "line" && (
                    <Line
                      data={points}
                      width={width}
                      height={chartHeight}
                      scaleX={scaleX}
                      scaleY={scaleY}
                      color={lineColor}
                      strokeWidth={settings.lineThickness}
                      displayGradient={true}
                    />
                  )}

                  {settings.indicators
                    .filter(indicator => indicator.enabled !== false)
                    .map((config, index) => (
                      <Indicator
                        key={index}
                        data={points}
                        width={width}
                        height={chartHeight}
                        scaleX={scaleX}
                        scaleY={scaleY}
                        config={config}
                        interval={this.props.interval}
                      />
                    ))}
                </Surface>
              </div>

              <OscillatorVisuals
                stock={this.props.stock.stock}
                width={width}
                height={`${oscillatorHeightInPercents}%`}
                oscillatorHeight={oscillatorHeight}
                indicators={this.props.settings.indicators}
                points={points}
                rawData={this.props.rawData}
                scaleX={scaleX}
                scaleY={scaleY}
                interval={this.props.interval}
                mouseX={this.props.mouseX}
                isHovered={this.props.isHovered}
                onEdit={this.props.onEditIndicator}
                onHide={index => {
                  this.props.dispatch({
                    type: "TOGGLE_INDICATOR",
                    payload: index
                  });
                }}
                onRemove={index =>
                  this.props.dispatch({
                    type: "REMOVE_INDICATOR",
                    payload: index
                  })
                }
              />
            </SurfaceContainer>
          </div>

          <Overlay {...this.props} lineColor={lineColor} />
        </div>

        <TourHint />
        <ABTest campaign="first_deposit_bonus">
          <BonusTimer />
        </ABTest>

        {this.props.interactive && (
          <ChartBottomNavigation
            social={<SocialTrading />}
            loading={this.props.loading}
            timeSpan={this.props.timeSpan}
            onTimeSpanChange={this.props.onTimeSpanChange}
            onBackToReality={this.props.onBackToReality}
            interval={this.props.interval}
            onIntervalChange={this.props.onIntervalChange}
            chartType={this.props.settings.chartType}
            isShownCandleModal={this.state.isShownCandleModal}
            toggleCandleModal={this.toggleCandleModal}
            stock={this.props.stock}
            now={this.props.lastPoint[2].date}
          />
        )}
      </div>
    );
  }
}

export default (props: Types.VisualProps) => (
  <ThemeConsumer>{theme => <Visuals {...props} theme={theme} />}</ThemeConsumer>
);



// WEBPACK FOOTER //
// ./src/components/Chart/NextGenDesktopVisuals.js